import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Paper,
  Tooltip,
  Divider,
  Checkbox,
  Grid,
  Card,
  CardContent,
  useTheme,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  useMediaQuery,
  Collapse,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Autocomplete,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import {
  Photo as PhotoIcon,
  VideoLibrary as VideoLibraryIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon, // <--- NUOVA ICONA PER TESTO
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  PlaylistAdd as PlaylistAddIcon,
  CloudUpload as CloudUploadIcon,
  CalendarToday as CalendarTodayIcon,
  Devices as DevicesIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  InfoOutlined as InfoIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  FilterListOff as FilterListOffIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Pause as PauseIcon,
  SmartDisplay as SmartDisplayIcon,
  History as HistoryIcon,
  Monitor as MonitorIcon,
  TouchApp as TouchAppIcon,
} from "@mui/icons-material";

import { styled, alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { format, isValid, set } from "date-fns";
import { it } from "date-fns/locale";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { motion, AnimatePresence } from "framer-motion";

import ContentCardV2 from "../../components/ImageCard";
import { InfoContext } from "../../context/InfoContext";
import Uploader from "../../components/Uploader";
import Schedule from "../../components/Schedule";
import Playlist from "../../components/Playlist";
import { monitorConfigurationApi, totemConfigurationApi } from "../../apiUser";
import WaitingDialog from "../../components/WaitingDialog";
import { mediaApi, contentSetApi } from "../../apiUser";
import { Form } from "react-router-dom";
import { monitorTheme, totemTheme } from "../../theme";

import video_fig from "../../video_fig.png";

// --- DEFINIZIONI PER LE ANIMAZIONI ---

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
};

function parseDate(dataString) {
  if (
    typeof dataString !== "string" ||
    !dataString.match(/^\d{4}\/\d{2}\/\d{2}$/)
  ) {
    console.error("Errore: La stringa data non è nel formato YYYY/MM/DD.");
    return null;
  }
  const correctedDateString = dataString.replace(/\//g, "-");
  const dateObject = new Date(correctedDateString);
  if (isNaN(dateObject.getTime())) {
    console.error(
      "Errore: Impossibile creare un oggetto Date valido dalla stringa fornita."
    );
    return null;
  }
  return dateObject;
}

// --- COMPONENTI HELPER CON DOCUMENTAZIONE ---

/**
 * Mostra un'icona basata sull'estensione del file.
 * Aggiornato per gestire file .txt
 */
const FileTypeIcon = ({ fileName, sx }) => {
  const extension = fileName?.split(".").pop().toLowerCase() || "";
  const baseSx = { verticalAlign: "middle", ...sx };

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension))
    return <PhotoIcon sx={baseSx} color="primary" />;
  if (["mp4", "mov", "avi", "wmv"].includes(extension))
    return <VideoLibraryIcon sx={baseSx} color="error" />;
  if (extension === "pdf")
    return <PictureAsPdfIcon sx={baseSx} color="warning" />;
  if (extension === "txt")
    return <DescriptionIcon sx={baseSx} color="info" />; // <--- GESTIONE TXT
  
  return null;
};

function RowActionMenu({ item, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleDelete = () => {
    onDelete(item.image_id);
    handleClose();
  };
  return (
    <>
      <IconButton aria-label="more actions" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleClose}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> Modifica
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Elimina
        </MenuItem>
      </Menu>
    </>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "original_name", label: "Nome" },
  { id: "content_set", label: "Set" },
  { id: "image_state", label: "Stato", align: "center", disableSorting: true },
  { id: "date_added", label: "Aggiunt/a Il" },
  { id: "play_date_start", label: "Inizia Dal" },
  { id: "play_date_end", label: "Fino al" },
  {
    id: "image_original_resolution",
    label: "Risoluzione",
    disableSorting: true,
  },
  { id: "image_original_size", label: "Dimensione", disableSorting: true },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={
              headCell.align ?? (headCell.id === "actions" ? "right" : "left")
            }
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              disabled={headCell.disableSorting}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

/**
 * Card per rappresentare un file nella vista a griglia.
 * Aggiornata per gestire la visualizzazione dei file TXT
 */
const FileCard = ({ item, isSelected, onCardClick, onCheckboxClick }) => {
  const theme = useTheme();
  const isScheduled = item.image_state === "WEB";
  const schedulingStatus = mediaScheduling(item);
  const statusColor = isScheduled ? "success" : "warning";
  const {
    color: schedulingColor,
    icon: schedulingIcon,
    label: schedulingLabel,
  } = getSchedulingStatusProps(schedulingStatus);

  const extension = item.original_name?.split(".").pop().toLowerCase();
  const isText = item.content_type === 'TXT';
  const isVideo = ['mp4', 'mov', 'avi', 'wmv'].includes(extension);

  return (
    <motion.div variants={itemVariants}>
      <Card
        onClick={onCardClick}
        variant="outlined"
        sx={{
          position: "relative",
          cursor: "pointer",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.light, 0.2)
            : "transparent",
          border: "1px solid",
          borderColor: isSelected ? "primary.main" : theme.palette.divider,
          transition: theme.transitions.create(
            ["background-color", "transform", "border-color"],
            {
              duration: theme.transitions.duration.short,
            }
          ),
          "&:hover": {
            transform: "translateY(-4px)",
            backgroundColor: isSelected
              ? alpha(theme.palette.primary.light, 0.2)
              : theme.palette.action.hover,
            "& .select-checkbox": { opacity: 1 },
          },
        }}
      >
        <Checkbox
          className="select-checkbox"
          checked={isSelected}
          onClick={onCheckboxClick}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 1,
            backgroundColor: "rgba(255,255,255,0.8)",
            opacity: isSelected ? 1 : 0,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.95)",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 140,
            backgroundColor: theme.palette.grey[100],
            overflow: "hidden",
            color: theme.palette.text.secondary
          }}
        >
          {/* LOGICA DI VISUALIZZAZIONE ANTEPRIMA */}
          {isText ? (
             <DescriptionIcon sx={{ fontSize: 64, color: theme.palette.info.main }} />
          ) : (
            <img
                src={isVideo ? video_fig : item.web_thumbnail_path}
                alt={item.original_name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
        </Box>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, flexGrow: 1 }}>
          <Stack spacing={1}>
            <Grid
              container
              spacing={1}
              alignItems="left"
              justifyContent="center"
              direction={"row"}
            >
              <Grid item>
                <Chip
                  label={item.image_state}
                  color={statusColor}
                  size="small"
                  sx={{ fontWeight: "bold" }}
                />
              </Grid>
              <Grid item>
                {isScheduled && (
                  <Chip
                    icon={schedulingIcon}
                    label={schedulingLabel}
                    color={schedulingColor}
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                )}
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileTypeIcon fileName={item.original_name} sx={{ mr: 0.5 }} />
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={item.original_name}
              >
                {item.original_name}
              </Typography>
            </Box>
            <Stack spacing={0.5} sx={{ pl: "28px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DevicesIcon sx={{ fontSize: 16 }} color="action" />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {item.content_set}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} color="action" />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Inizia:{" "}
                  {format(new Date(item.play_date_start), "dd MMM yyyy", {
                    locale: it,
                  })}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const mediaScheduling = (item) => {
  if (item == null) return "Unknown";
  console.log('Evaluating scheduling for item:', item);
  const currentDateTime = new Date();
  const currentDayOfWeek = currentDateTime.getDay();
  const playDaysArray = item.play_days.split("").map((d) => d === "1");
  const isTodayPlayable = playDaysArray[(currentDayOfWeek + 6) % 7];
  const startDate = new Date(item.play_date_start.replace(/\//g, "-"));
  const endDate = new Date(item.play_date_end.replace(/\//g, "-"));
  endDate.setHours(23, 59, 59, 999);
  const startTime = new Date(`1970-01-01T${item.play_time_start}`);
  const endTime = new Date(`1970-01-01T${item.play_time_end}`);
  const currentTime = new Date(
    `1970-01-01T${currentDateTime.toTimeString().slice(0, 8)}`
  );

  if (currentDateTime < startDate) {
    return "Scheduled";
  } else if (currentDateTime > endDate) {
    return "Expired";
  } else {
    if (isTodayPlayable && currentTime >= startTime && currentTime <= endTime) {
      return "Playing";
    } else if (isTodayPlayable && currentTime < startTime) {
      return "Scheduled";
    } else {
      return "Paused";
    }
  }
};

const getSchedulingStatusProps = (status) => {
  switch (status) {
    case "Playing":
      return {
        color: "success",
        icon: <SmartDisplayIcon />,
        label: "In Riproduzione",
      };
    case "Scheduled":
      return { color: "info", icon: <ScheduleIcon />, label: "Programmato" };
    case "Paused":
      return { color: "warning", icon: <PauseIcon />, label: "In Pausa" };
    case "Expired":
      return { color: "error", icon: <HistoryIcon />, label: "Scaduto" };
    default:
      return { color: "default", icon: <InfoIcon />, label: status };
  }
};

function Home() {
  const { info } = useContext(InfoContext);
  const [content, setContent] = useState([]);
  const [selected, setSelected] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("original_name");
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Caricamento in corso..."
  );
  const [mediaMode, setMediaMode] = useState("monitor");
  const currentTheme = mediaMode === "monitor" ? monitorTheme : totemTheme;
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMediaMode(newMode);
      setSomethingHasChanged(true);
      setSelected([]);
    }
  };
  
  const [filters, setFilters] = useState({
    fileType: "",
    contentSet: "",
    playDateStart: null,
    playDateEnd: null,
    deviceIds: "",
    playDays: "",
    schedulingStatus: "", 
  });
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [somethingHasChanged, setSomethingHasChanged] = useState(true);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [contentSets, setContentSets] = useState([]);

  const isScreenTall = useMediaQuery("(min-height:1081px)");
  const containerMaxHeight = isScreenTall ? "88dvh" : "86dvh";

  const filteredContent = useMemo(() => {
    if (!content || content.length === 0) return [];
    return content.filter((item) => {
      // Filtro Tipo File
      if (filters.fileType) {
        const extension =
          item.original_name?.split(".").pop().toLowerCase() || "";
        
        if (filters.fileType === "image" && !["jpg", "jpeg", "png"].includes(extension))
          return false;
        if (filters.fileType === "video" && !["mp4", "mov", "avi"].includes(extension))
          return false;
        if (filters.fileType === "pdf" && extension !== "pdf") return false;
        if (filters.fileType === "text" && item.content_type !== "TXT") return false; // <--- NUOVO FILTRO TESTO
      }
      if (showOnlyPending) {
        const stateStr = (item.image_state ?? item.state ?? "")
          .toString()
          .toUpperCase();
        if (stateStr !== "PENDING") return false;
      }
      if (
        filters.contentSet &&
        !item.content_set
          .toLowerCase()
          .includes(filters.contentSet.toLowerCase())
      ) {
        return false;
      }
      if (filters.playDateStart && isValid(filters.playDateStart)) {
        if (new Date(item.play_date_start) < filters.playDateStart)
          return false;
      }
      if (filters.playDateEnd && isValid(filters.playDateEnd)) {
        if (new Date(item.play_date_end) > filters.playDateEnd) return false;
      }
      if (
        filters.deviceIds &&
        !item.device_ids?.toString().includes(filters.deviceIds)
      ) {
        return false;
      }
      if (
        filters.playDays &&
        !item.play_days?.toString().includes(filters.playDays)
      ) {
        return false;
      }
      if (filters.schedulingStatus) {
        if (item.image_state !== "WEB") return false;
        const currentStatus = mediaScheduling(item);
        if (currentStatus !== filters.schedulingStatus) {
          return false;
        }
      }
      return true;
    });
  }, [content, filters, showOnlyPending]);

  useEffect(() => {
    if (!somethingHasChanged) {
      return;
    }
    setContent([]);
    const getConfigurazione = async () => {
      try {
        setIsLoading(true);
        let response;
        if (mediaMode === "totem") {
          setLoadingMessage("Caricamento configurazione totem...");
          response = await totemConfigurationApi.get();
          // for each element in response.data, for each key remove the prefix 'totem_'
          response.data = response.data.map(item => {
            const newItem = {};
            for (const key in item) {
              if (key.startsWith('totem_')) {
                newItem[key.substring(6)] = item[key];
              } else {
                newItem[key] = item[key];
              }
            }
            return newItem;
          });
          console.log("Configurazione totem caricata:", response.data);
        } else {
          setLoadingMessage("Caricamento configurazione monitor...");
          response = await monitorConfigurationApi.get();
          console.log("Configurazione monitor caricata:", response.data);
        }

        setIsLoading(false);
        setSomethingHasChanged(false); 
        setContent(response.data);
      } catch (error) {
        setIsLoading(false);
        setContent([]);
        console.error("Errore nel recupero della configurazione:", error);
      }
    };
    getConfigurazione();
  }, [somethingHasChanged]);

  useEffect(() => {
    setLoadingMessage("Caricamento content set...");
    contentSetApi
      .get()
      .then((response) => setContentSets(response.data || []))
      .catch((err) => {
        console.error("Errore nel recupero dei content set:", err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleFilterChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleDateChange = (name, date) =>
    setFilters((prev) => ({ ...prev, [name]: date }));

  const handleResetFilters = () => {
    setFilters({
      fileType: "",
      contentSet: "",
      playDateStart: null,
      playDateEnd: null,
      deviceIds: "",
      playDays: "",
      schedulingStatus: "",
    });
    setShowOnlyPending(false);
  };
  const handleViewChange = (e, newView) => {
    if (newView !== null) setViewMode(newView);
  };
  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleCardClick = (item) => {
    setSelected((prev) => {
      const isSelected = prev.includes(item.image_id);
      if (isSelected) {
        return prev.filter((id) => id !== item.image_id);
      } else {
        return [...prev, item.image_id];
      }
    });
  };

  const handleDeleteSelected = async () => {
    setIsLoading(true);
    const totalSelected = selected.length;
    const BATCH_SIZE = 10; 

    if (previewItem && selected.includes(previewItem.image_id)) {
      setPreviewItem(null);
    }

    try {
      if (totalSelected > BATCH_SIZE) {
        const totalBatches = Math.ceil(totalSelected / BATCH_SIZE);

        for (let i = 0; i < totalSelected; i += BATCH_SIZE) {
          const currentBatchNumber = i / BATCH_SIZE + 1;
          const batch = selected.slice(i, i + BATCH_SIZE);

          setLoadingMessage(
            `Eliminazione lotto ${currentBatchNumber} di ${totalBatches}...`
          );

          const deletePromises = batch.map((id) => mediaApi.delete(id));
          await Promise.all(deletePromises); 
        }
      } else {
        setLoadingMessage("Eliminazione degli elementi selezionati...");
        const deletePromises = selected.map((id) => mediaApi.delete(id));
        await Promise.all(deletePromises);
      }

      await monitorConfigurationApi.delete();

      console.log("Elementi eliminati con successo");
      alert(`${totalSelected} elementi eliminati con successo.`);

      setSomethingHasChanged(true); 
    } catch (error) {
      console.error("Errore durante l'eliminazione degli elementi:", error);
      alert("Si è verificato un errore durante l'eliminazione.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSelected = async () => {
    setIsLoading(true);
    setLoadingMessage("Conferma degli elementi selezionati...");

    try {
      const response = await monitorConfigurationApi.confirm();
      if (response) {
        alert("Elementi confermati con successo.");
        setSomethingHasChanged(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Errore durante la conferma degli elementi:", error);
      alert("Errore durante la conferma degli elementi selezionati.");
      setIsLoading(false);
    }
  };

  const handleDeleteFromCard = (id) => {
    setContent((c) => c.filter((item) => item.image_id !== id));
    setSelected((s) => s.filter((selId) => selId !== id));
    setPreviewItem(null);
  };
  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleCheckboxClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredContent.map((n) => n.image_id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleUpload = async (filesToUpload, uploadMetadata) => {
    setIsLoading(true);
    const totalFiles = filesToUpload.length;
    const BATCH_SIZE = 10; 
    console.log("Files to upload:", filesToUpload); 
    console.log("Upload metadata:", uploadMetadata);
    try {
      if (totalFiles > BATCH_SIZE) {
        const totalBatches = Math.ceil(totalFiles / BATCH_SIZE);

        for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
          const currentBatchNumber = i / BATCH_SIZE + 1;
          const batch = filesToUpload.slice(i, i + BATCH_SIZE);

          setLoadingMessage(
            `Caricamento lotto ${currentBatchNumber} di ${totalBatches}...`
          );

          const uploadPromises = batch.map((file) =>
            mediaApi.upload({file, uploadMetadata})
          );

          await Promise.all(uploadPromises); 
        }
      } else {
        setLoadingMessage("Caricamento file...");
        const uploadPromises = filesToUpload.map((file) =>
          mediaApi.upload({ file, uploadMetadata })
        );
        await Promise.all(uploadPromises);
      }

      alert(`${totalFiles} file caricati con successo!`);
      setSomethingHasChanged(true); 
    } catch (err) {
      console.error("Errore durante l'upload:", err);
      alert("Si è verificato un errore durante il caricamento.");
    } finally {
      setIsLoading(false);
      setUploaderOpen(false); 
    }
  };

  const handleSaveSchedule = async (scheduleData) => {
    setIsLoading(true);
    const totalSelected = selected.length;
    const BATCH_SIZE = 10; 

    try {
      if (totalSelected > BATCH_SIZE) {
        const totalBatches = Math.ceil(totalSelected / BATCH_SIZE);

        for (let i = 0; i < totalSelected; i += BATCH_SIZE) {
          const currentBatchNumber = i / BATCH_SIZE + 1;
          const batch = selected.slice(i, i + BATCH_SIZE);

          setLoadingMessage(
            `Aggiornamento lotto ${currentBatchNumber} di ${totalBatches}...`
          );

          const batchPromises = batch.map((id) =>
            mediaApi.updateSchedule(id, scheduleData)
          );
          await Promise.all(batchPromises); 
        }
      } else {
        setLoadingMessage("Aggiornamento della pianificazione...");
        const selectedPromises = selected.map((id) =>
          mediaApi.updateSchedule(id, scheduleData)
        );
        await Promise.all(selectedPromises);
      }

      console.log(
        `Pianificazione aggiornata con successo per ${totalSelected} elementi.`
      );
      alert(`Pianificazione aggiornata per ${totalSelected} elementi!`);
      setSomethingHasChanged(true);
    } catch (error) {
      console.error(
        "Errore durante l'aggiornamento della pianificazione:",
        error
      );
      alert("Si è verificato un errore durante l'aggiornamento.");
    } finally {
      setIsLoading(false);
      setScheduleOpen(false); 
    }
  };

  const handleSavePlaylist = async (distributionData) => {
    setIsLoading(true);
    const totalSelected = selected.length;
    const BATCH_SIZE = 10; 

    try {
      if (totalSelected > BATCH_SIZE) {
        const totalBatches = Math.ceil(totalSelected / BATCH_SIZE);

        for (let i = 0; i < totalSelected; i += BATCH_SIZE) {
          const currentBatchNumber = i / BATCH_SIZE + 1;
          const batch = selected.slice(i, i + BATCH_SIZE);

          setLoadingMessage(
            `Aggiornamento distribuzione lotto ${currentBatchNumber} di ${totalBatches}...`
          );

          const batchPromises = batch.map((id) =>
            mediaApi.updatePlaylist(id, distributionData)
          );
          await Promise.all(batchPromises); 
        }
      } else {
        setLoadingMessage("Aggiornamento della distribuzione...");
        const selectedPromises = selected.map((id) =>
          mediaApi.updatePlaylist(id, distributionData)
        );
        await Promise.all(selectedPromises);
      }

      console.log(
        `Distribuzione aggiornata con successo per ${totalSelected} elementi.`
      );
      alert(`Distribuzione aggiornata per ${totalSelected} elementi!`);
      setSomethingHasChanged(true); 
    } catch (error) {
      console.error(
        "Errore durante l'aggiornamento della distribuzione:",
        error
      );
      alert(
        "Si è verificato un errore durante l'aggiornamento della distribuzione."
      );
    } finally {
      setIsLoading(false);
      setPlaylistOpen(false); 
    }
  };

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
        <WaitingDialog open={isLoading} message={loadingMessage} />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <ToggleButtonGroup
              value={mediaMode}
              exclusive
              onChange={handleModeChange}
              aria-label="media mode"
              color="primary"
            >
              <ToggleButton value="monitor" sx={{ px: 3 }}>
                <MonitorIcon sx={{ mr: 1 }} />
                Monitor
              </ToggleButton>
              <ToggleButton value="totem" sx={{ px: 3 }}>
                <TouchAppIcon sx={{ mr: 1 }} />
                Totem
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Grid
            container
            spacing={3}
            sx={{ mt: 1, mb: 1, ml: 1, mr: 1 }}
            key={mediaMode}
          >
            <Grid
              item
              size={{ xs: 12, md: previewItem ? 9 : 12 }}
              sx={{ transition: "width 0.3s ease-in-out" }}
            >
              <Paper
                sx={{ width: "100%", mb: 2, borderRadius: 2 }}
                variant="outlined"
              >
                <Toolbar sx={{ px: "16px !important", p: 2 }}>
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < filteredContent.length
                    }
                    checked={
                      filteredContent.length > 0 &&
                      selected.length === filteredContent.length
                    }
                    onChange={handleSelectAllClick}
                  />
                  <Typography
                    sx={{ flex: 0.2, ml: 2 }}
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {selected.length > 0
                      ? `${selected.length} selezionati`
                      : `${filteredContent.length} elementi trovati`}
                  </Typography>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    sx={{ mr: 2 }}
                    onChange={handleViewChange}
                    aria-label="view mode"
                  >
                    <ToggleButton value="grid" aria-label="grid view">
                      <GridViewIcon />
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="table view">
                      <ViewListIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {selected.length > 0 ? (
                    <>
                      <Tooltip title="Programma">
                        <IconButton onClick={() => setScheduleOpen(true)}>
                          <ScheduleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Distribuzione">
                        <IconButton onClick={() => setPlaylistOpen(true)}>
                          <PlaylistAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina Selezionati">
                        <IconButton
                          onClick={handleDeleteSelected}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        ml: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Tooltip title="Filtra Contenuti">
                          <Button
                            variant="outlined"
                            sx={{ height: 50 }}
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                          >
                            Filtri
                          </Button>
                        </Tooltip>
                        <Button
                          variant="contained"
                          sx={{ height: 50 }}
                          startIcon={<CloudUploadIcon />}
                          disableElevation
                          onClick={() => setUploaderOpen(true)}
                        >
                          Upload
                        </Button>
                      </Box>

                      <Box>
                        <Tooltip title="Tutti i media 'Pending' saranno inviati ai rispettivi device.">
                          <Button
                            variant="contained"
                            onClick={() => handleConfirmSelected()}
                            sx={{ height: 50 }}
                            color="success"
                            disabled={isLoading}
                            startIcon={<CheckCircleOutlineIcon />}
                          >
                            Conferma
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </Toolbar>

                <Collapse in={showFilters}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Tipo File</InputLabel>
                          <Select
                            name="fileType"
                            value={filters.fileType}
                            label="Tipo File"
                            onChange={handleFilterChange}
                          >
                            <MenuItem value="">
                              <em>Tutti</em>
                            </MenuItem>
                            <MenuItem value="image">Immagine</MenuItem>
                            <MenuItem value="video">Video</MenuItem>
                            <MenuItem value="text">Testo</MenuItem> {/* NUOVA OPZIONE */}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Stato Riproduzione</InputLabel>
                          <Select
                            name="schedulingStatus"
                            value={filters.schedulingStatus}
                            label="Stato Riproduzione"
                            onChange={handleFilterChange}
                          >
                            <MenuItem value="">
                              <em>Tutti</em>
                            </MenuItem>
                            <MenuItem value="Playing">In Riproduzione</MenuItem>
                            <MenuItem value="Scheduled">Programmato</MenuItem>
                            <MenuItem value="Paused">In Pausa</MenuItem>
                            <MenuItem value="Expired">Scaduto</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showOnlyPending}
                              onChange={() =>
                                setShowOnlyPending(!showOnlyPending)
                              }
                            />
                          }
                          label="Mostra Solo Pending"
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <Autocomplete
                          options={contentSets}
                          getOptionLabel={(option) =>
                            option.content_set_name || ""
                          }
                          key={filters.contentSet}
                          value={
                            contentSets.find(
                              (cs) => cs.content_set_name === filters.contentSet
                            ) || null
                          }
                          onChange={(event, newValue) =>
                            handleFilterChange({
                              target: {
                                name: "contentSet",
                                value: newValue
                                  ? newValue.content_set_name
                                  : "",
                              },
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cerca un content set"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <DatePicker
                          label="Inizia Dopo Il"
                          value={filters.playDateStart}
                          onChange={(date) =>
                            handleDateChange("playDateStart", date)
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <DatePicker
                          label="Termina Prima Del"
                          value={filters.playDateEnd}
                          onChange={(date) =>
                            handleDateChange("playDateEnd", date)
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          startIcon={<FilterListOffIcon />}
                          onClick={handleResetFilters}
                        >
                          Resetta Filtri
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>

                <Divider />

                <AnimatePresence mode="wait">
                  <motion.div
                    key={viewMode}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0 }}
                    variants={containerVariants}
                  >
                    {viewMode === "grid" ? (
                      <Box
                        sx={{
                          p: 3,
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(220px, 1fr))",
                          maxHeight: containerMaxHeight,
                          gap: 3,
                          overflowY: "auto",
                        }}
                      >
                        {filteredContent.map((item) => (
                          <Box
                            sx={{ position: "relative" }}
                            onMouseOver={() =>
                              selected.length < 1 && setPreviewItem(item)
                            }
                            onClick={() => setPreviewItem(item)}
                          >
                            <FileCard
                              key={item.image_id}
                              item={item}
                              isSelected={isSelected(item.image_id)}
                              onCardClick={() => handleCardClick(item)}
                              onCheckboxClick={(e) =>
                                handleCheckboxClick(e, item.image_id)
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <TableContainer
                        sx={{
                          maxHeight: containerMaxHeight,
                          overflowY: "auto",
                        }}
                      >
                        <Table stickyHeader>
                          <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={filteredContent.length}
                          />
                          <motion.tbody>
                            {stableSort(
                              filteredContent,
                              getComparator(order, orderBy)
                            ).map((item) => {
                              const isItemSelected = isSelected(item.image_id);
                              const isScheduled = item.image_state === "WEB";
                              const schedulingStatus = mediaScheduling(item);
                              const statusColor = isScheduled
                                ? "success"
                                : "warning";
                              const {
                                color: schedulingColor,
                                icon: schedulingIcon,
                                label: schedulingLabel,
                              } = getSchedulingStatusProps(schedulingStatus);
                              return (
                                <TableRow
                                  component={motion.tr}
                                  key={item.image_id}
                                  variants={itemVariants}
                                  hover
                                  onMouseOver={() =>
                                    selected.length < 1 && setPreviewItem(item)
                                  }
                                  onClick={(e) => {
                                    handleCheckboxClick(e, item.image_id);
                                    setPreviewItem(item);
                                  }}
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  tabIndex={-1}
                                  selected={isItemSelected}
                                  sx={{ cursor: "pointer" }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      color="primary"
                                      checked={isItemSelected}
                                    />
                                  </TableCell>
                                  <TableCell component="th" scope="row">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {/* GESTIONE AVATAR NELLA TABELLA */}
                                      {item.original_name.endsWith(".txt") ? (
                                        <Avatar
                                           variant="rounded"
                                           sx={{ mr: 2, width: 48, height: 36, bgcolor: 'action.hover' }}
                                        >
                                           <DescriptionIcon fontSize="small" color="info" />
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                            variant="rounded"
                                            src={item.web_thumbnail_path}
                                            sx={{ mr: 2, width: 48, height: 36 }}
                                        />
                                      )}
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        {item.original_name}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="left">
                                    {item.content_set}
                                  </TableCell>
                                  <TableCell align="left">
                                    <Grid
                                      container
                                      spacing={1}
                                      alignItems="left"
                                      justifyContent="center"
                                      direction={"column"}
                                    >
                                      <Grid item>
                                        <Chip
                                          label={item.image_state}
                                          color={statusColor}
                                          size="small"
                                          sx={{ fontWeight: "bold" }}
                                        />
                                      </Grid>
                                      <Grid item>
                                        {isScheduled && (
                                          <Chip
                                            icon={schedulingIcon}
                                            label={schedulingLabel}
                                            color={schedulingColor}
                                            size="small"
                                            sx={{ fontWeight: "bold" }}
                                          />
                                        )}
                                      </Grid>
                                    </Grid>
                                  </TableCell>
                                  <TableCell align="left">
                                    {format(
                                      parseDate(item.date_added),
                                      "dd MMM yyyy",
                                      { locale: it }
                                    )}
                                  </TableCell>
                                  <TableCell align="left">
                                    {format(
                                      parseDate(item.play_date_start),
                                      "dd MMM yyyy",
                                      { locale: it }
                                    )}
                                  </TableCell>
                                  <TableCell align="left">
                                    {format(
                                      parseDate(item.play_date_end),
                                      "dd MMM yyyy",
                                      { locale: it }
                                    )}
                                  </TableCell>
                                  <TableCell align="left">
                                    {item.image_original_resolution}
                                  </TableCell>
                                  <TableCell align="left">
                                    {item.image_original_size.toFixed(2)} MB
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </motion.tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </motion.div>
                </AnimatePresence>
              </Paper>
            </Grid>

            <AnimatePresence>
              {previewItem && (
                <Grid
                  item
                  size={{ xs: 12, md: 3 }}
                  component={motion.div}
                  sx={{ mt: 0.1, height: containerMaxHeight }}
                >
                  <Paper
                    sx={{
                      position: "sticky",
                      width: "100%",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                    variant="outlined"
                  >
                    <ContentCardV2
                      item={previewItem}
                      onDelete={handleDeleteFromCard}
                    />
                  </Paper>
                </Grid>
              )}
            </AnimatePresence>
          </Grid>
        </motion.div>
        <Uploader
          open={uploaderOpen}
          onClose={() => setUploaderOpen(false)}
          onSave={handleUpload}
          mediaMode={mediaMode}
        />
        <Schedule
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          onSave={handleSaveSchedule}
          selectedCount={selected.length}
          selectedItems={
            selected.length > 0
              ? content.filter((item) => selected.includes(item.image_id))
              : []
          }
        />
        <Playlist
          configuration={info?.selectedClient?.configurazione || []}
          open={playlistOpen}
          onClose={() => setPlaylistOpen(false)}
          onSave={handleSavePlaylist}
          selectedCount={selected.length}
          selectedItems={
            selected.length > 0
              ? content.filter((item) => selected.includes(item.image_id))
              : []
          }
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default Home;